import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useAddManualBlockedDate } from '../hooks/useAddManualBlockedDate';
import { toast } from 'react-toastify';

const BookingCalendar = ({ property, bookings = [], bookedDates = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedRange, setSelectedRange] = useState({ start: null, end: null });
  const [selectionMode, setSelectionMode] = useState(null); // 'book' or 'unbook'
  const { mutate: addManualBlockedDate } = useAddManualBlockedDate();

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateBooked = (date) => {
    const dateStr = formatDateLocal(date);
    return bookedDates.includes(dateStr);
  };

  const isDatePast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateToday = (date) => {
    return date.toDateString() === new Date().toDateString();
  };

  const isDateInRange = (date) => {
    if (!selectedRange.start || !selectedRange.end) return false;
    return date >= selectedRange.start && date <= selectedRange.end;
  };

  const hasBookedDateInRange = () => {
    if (!selectedRange.start || !selectedRange.end) return false;
    
    for (let d = new Date(selectedRange.start); d <= selectedRange.end; d.setDate(d.getDate() + 1)) {
      const dateStr = formatDateLocal(d);
      if (bookedDates.includes(dateStr)) {
        return true;
      }
    }
    return false;
  };

  const handleDateClick = (day) => {
    if (!selectionMode) return;
    if (isDatePast(day) && !isDateToday(day)) return;

    if (!selectedRange.start) {
      // First click: register as start date
      setSelectedRange({ start: day, end: null });
    } else if (!selectedRange.end) {
      // Second click: register as end date
      if (day < selectedRange.start) {
        setSelectedRange({ start: day, end: selectedRange.start });
      } else {
        setSelectedRange({ ...selectedRange, end: day });
      }
    } else {
      // Both dates selected: reset and start new selection
      setSelectedRange({ start: day, end: null });
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleBookDates = () => {
    if (!selectedRange.start || !selectedRange.end) {
      toast.warning('Please select a date range');
      return;
    }

    // Check for overlap with booked dates
    if (hasBookedDateInRange()) {
      toast.error('Cannot block dates that overlap with existing customer bookings');
      return;
    }

    addManualBlockedDate(
      { propertyId: property.id, startDate: selectedRange.start, endDate: selectedRange.end },
      {
        onSuccess: (response) => {
          toast.success(response?.message || 'Dates blocked successfully!');
          setSelectedRange({ start: null, end: null });
          setSelectionMode(null);
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to block dates');
        },
      }
    );
  };

  const handleCancel = () => {
    setSelectionMode(null);
    setSelectedRange({ start: null, end: null });
  };

  const monthName = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), i));
  }

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className="bg-white rounded-lg border border-gray-300 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Calendar className="text-prose-brand" size={24} />
        <h3 className="text-xl font-bold text-prose-brand">Booking Calendar</h3>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-semibold text-prose-brand">{monthName}</h4>
        <div className="flex gap-2">
          <button
            onClick={handlePrevMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition duration-200"
          >
            <ChevronLeft size={20} className="text-prose-brand" />
          </button>
          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition duration-200"
          >
            <ChevronRight size={20} className="text-prose-brand" />
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-2 mb-3">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="text-center text-sm font-bold text-prose-brand p-3">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2 mb-6">
        {weeks.map((week, weekIdx) =>
          week.map((day, dayIdx) => {
            const isBooked = day && isDateBooked(day);
            const isPast = day && isDatePast(day);
            const isToday = day && isDateToday(day);
            const isInRange = day && isDateInRange(day);
            const isCurrentMonth = day && day.getMonth() === currentDate.getMonth();

            return (
              <button
                key={`${weekIdx}-${dayIdx}`}
                onClick={() => day && handleDateClick(day)}
                disabled={!day || (isPast && !isToday) || !selectionMode}
                className={`h-14 rounded-lg text-base font-semibold transition duration-200 flex items-center justify-center ${
                  !day
                    ? 'bg-transparent'
                    : isToday
                    ? 'bg-blue-100 border-2 border-blue-500 text-blue-900'
                    : isBooked
                    ? 'bg-yellow-100 border-2 border-yellow-500 text-yellow-900 cursor-not-allowed'
                    : isPast
                    ? 'bg-gray-100 text-prose-black/30 cursor-not-allowed'
                    : isInRange
                    ? 'bg-prose-gold/20 border-2 border-prose-gold text-prose-brand'
                    : isCurrentMonth
                    ? 'bg-gray-100 text-prose-black hover:border-2 hover:border-prose-gold/50'
                    : 'bg-transparent text-prose-black/30'
                }`}
                title={day ? day.toDateString() : ''}
              >
                {day && <span>{day.getDate()}</span>}
              </button>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className="border-t border-gray-300 pt-4 mb-6 space-y-2">
        <h4 className="text-sm font-semibold text-prose-black mb-3">Legend:</h4>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-4 h-4 bg-yellow-100 border-2 border-yellow-500 rounded"></div>
          <span className="text-prose-black">Customer Booking</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-4 h-4 bg-blue-100 border-2 border-blue-500 rounded"></div>
          <span className="text-prose-black">Today</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-4 h-4 bg-gray-100 rounded"></div>
          <span className="text-prose-black">Available</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-4 h-4 bg-transparent border-2 border-prose-black/30 rounded"></div>
          <span className="text-prose-black/30">Past Date</span>
        </div>
        <div className="bg-white border border-black/20 rounded p-2 mt-3">
          <p className="text-xs text-prose-black">
            Blocked dates are now displayed in the <span className="font-semibold text-prose-brand">Manual Blocked Dates</span> section below the calendar.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="border-t border-gray-300 pt-4 space-y-3">
        {!selectionMode ? (
          <button
            onClick={() => setSelectionMode('book')}
            className="w-full px-4 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 transition duration-200 font-semibold"
          >
            Block Date Range
          </button>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-prose-black/70">
              {selectedRange.start && selectedRange.end ? (
                <p>
                  Selected: {selectedRange.start.toLocaleDateString()} to{' '}
                  {selectedRange.end.toLocaleDateString()}
                </p>
              ) : selectedRange.start ? (
                <p>Start: {selectedRange.start.toLocaleDateString()} - Click end date</p>
              ) : (
                <p>Click to select date range</p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBookDates}
                disabled={!selectedRange.start || !selectedRange.end}
                className="flex-1 px-4 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 disabled:bg-prose-brand/50 disabled:cursor-not-allowed transition duration-200 font-semibold"
              >
                Confirm Block
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2 border border-gray-300 text-prose-brand rounded-lg hover:bg-gray-100 transition duration-200 font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingCalendar;
